'use client';

import { TrackPromotionImpression } from '@components/TrackPromotionImpression/TrackPromotionImpression';
import { usePromoTrackingEvents } from '@components/TrackPromotionImpression/usePromoTrackingEvents';
import { useCookieSettings } from '@lib/hooks/useCookieSettings';
import {
  HeroBannerCard,
  HeroBanner as HeroBannerType,
} from '@lib/types/cms/HeroBanner';
import { isDefined } from '@lib/util/isDefined';
import { toNonNullable } from '@lib/util/toNonNullable';
import { useRouter } from 'next/navigation';
import { HeroBanner as HeroBannerUI } from 'ui-components';
import { useHeroCountdown } from '../HomepageHero/useHeroCountdown';

type HeroBannerProps = {
  data: HeroBannerType;
  position: number;
};

const HeroBanner = ({ data, position }: HeroBannerProps) => {
  const { functionalCategoryEnabled, targetingCategoryEnabled } =
    useCookieSettings();
  const hasCookieConsent =
    targetingCategoryEnabled && functionalCategoryEnabled;

  const heroBannerFirstCard: HeroBannerCard = data.cards?.[0];
  const heroBannerSecondCard: HeroBannerCard = data.cards?.[1];

  const { promoClickTrackingEvent, promoViewTrackingEvent } =
    usePromoTrackingEvents({
      title: data.titleInternal || '',
      typename: 'HeroBanner',
      position,
      creativeTitle: toNonNullable(
        heroBannerFirstCard?.image?.title || heroBannerFirstCard?.video?.title
      ),
      trackAsPromo: data.trackAsPromo ?? false,
      ctas: heroBannerFirstCard.ctas.map((cta) => cta.label).filter(isDefined),
    });

  const router = useRouter();
  const onExpiredCountdown = () => {
    router.refresh();
  };

  const {
    expired: expiredFirst,
    hasCountdown: hasCountdownFirst,
    countdown: countdownFirst,
  } = useHeroCountdown(
    heroBannerFirstCard.countdownTo ?? null,
    onExpiredCountdown
  );

  const {
    expired: expiredSecond,
    hasCountdown: hasCountdownSecond,
    countdown: countdownSecond,
  } = useHeroCountdown(
    heroBannerSecondCard?.countdownTo ?? null,
    onExpiredCountdown
  );

  const showCountdownFirst = hasCountdownFirst && !expiredFirst;
  const showCountdownSecond = hasCountdownSecond && !expiredSecond;

  return (
    <TrackPromotionImpression
      onImpression={promoViewTrackingEvent}
      shouldTrack={data.trackAsPromo ?? false}
    >
      <HeroBannerUI
        microcopy={{
          salesEnd: data.labels?.['sales-end'] ?? '',
          copyCouponCode: data.labels?.['copy-coupon-code'] ?? '',
          couponCodeCopied: data.labels?.['coupon-code-copied'] ?? '',
          close: data.labels?.['close'] ?? '',
          days: data.labels?.['countdown-days'] ?? '',
          hours: data.labels?.['countdown-hours'] ?? '',
          minutes: data.labels?.['countdown-minutes'] ?? '',
          seconds: data.labels?.['countdown-seconds'] ?? '',
        }}
        alignment={data.alignment}
        theme={data.theme}
        cards={data.cards.map((card, index) => ({
          subHeader: card.subheader,
          title: card.heading,
          coupon: card.coupon,
          image: card.image?.url
            ? {
                url: card.image?.url ?? '',
                alt: card.image?.alt ?? '',
                height: toNonNullable(card.image?.height),
                width: toNonNullable(card.image?.width),
              }
            : undefined,
          video: card.video?.url
            ? {
                url: card.video.url,
                title: card.video.title ?? '',
                description: card.video.description ?? '',
                fallbackImage: card.video.fallbackImageUrl ?? '',
              }
            : undefined,
          embeddedVideo: card.embeddedVideo?.url
            ? {
                url: card.embeddedVideo.url,
                fallbackImage: card.embeddedVideo.fallbackImageUrl ?? '',
                title: card.embeddedVideo.title ?? '',
              }
            : undefined,
          hasCookieConsent,
          description: card.description ?? '',
          countdownDuration:
            index === 0
              ? showCountdownFirst
                ? countdownFirst
                : undefined
              : showCountdownSecond
                ? countdownSecond
                : undefined,
          actions: card.ctas.map((cta) => ({
            label: cta.label,
            url: cta.url,
            openInNewTab: toNonNullable(cta.openInNewTab),
            style: cta.appearance,
          })),
        }))}
        onCtaClick={promoClickTrackingEvent}
      />
    </TrackPromotionImpression>
  );
};

export default HeroBanner;
